import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import API_URL from './config';
import './QueueReport.css';

const QueueReport = ({ token, userRole, username }) => {
  // Estados para filtros
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filaSelecionada, setFilaSelecionada] = useState('');
  const [ramalSelecionado, setRamalSelecionado] = useState('');
  const [statusSelecionado, setStatusSelecionado] = useState('');
  
  // Estados para dados
  const [metricas, setMetricas] = useState(null);
  const [chamadas, setChamadas] = useState([]);
  const [filasDisponiveis, setFilasDisponiveis] = useState([]);
  const [paginacao, setPaginacao] = useState({ pagina_atual: 1, total_paginas: 1, total: 0 });
  
  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [loadingFilas, setLoadingFilas] = useState(true);
  const [error, setError] = useState('');
  const [visuMode, setVisuMode] = useState('cards');
  const [serverId, setServerId] = useState('');
  const [availableServers, setAvailableServers] = useState([]);
  const isAdmin = userRole === 'admin';

  // Tradução de status para PT-BR
  const traduzirStatus = (status) => {
    const traducoes = {
      'ANSWERED': 'Atendida',
      'ABANDONED': 'Abandonada',
      'NO ANSWER': 'Não Atendida',
      'BUSY': 'Ocupado',
      'FAILED': 'Falha',
      'CONGESTION': 'Congestionamento'
    };
    return traducoes[status] || status;
  };

  // Formatar data e hora para DD/MM/AAAA - HH:MM:SS
  const formatarDataHora = (dataHora) => {
    if (!dataHora) return '-';
    try {
      const data = new Date(dataHora);
      if (isNaN(data.getTime())) return '-';
      const dia = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();
      const horas = String(data.getHours()).padStart(2, '0');
      const minutos = String(data.getMinutes()).padStart(2, '0');
      const segundos = String(data.getSeconds()).padStart(2, '0');
      return `${dia}/${mes}/${ano} - ${horas}:${minutos}:${segundos}`;
    } catch (e) {
      return '-';
    }
  };

  // Formatar data ISO para DD/MM/AAAA
  const formatarDataISO = (dataISO) => {
    if (!dataISO) return '';
    try {
      const [ano, mes, dia] = dataISO.split('-');
      return `${dia}/${mes}/${ano}`;
    } catch (e) {
      return '';
    }
  };

  // Buscar servidores para admin
  useEffect(() => {
    if (!isAdmin) return;
    const fetchServers = async () => {
      try {
        const resp = await axios.get(`${API_URL}/users/servers/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setAvailableServers(resp.data);
          setServerId(resp.data[0].id.toString());
        }
      } catch (e) {
        console.error('Erro ao buscar servidores:', e);
      }
    };
    fetchServers();
  }, [isAdmin, token]);

  // Definir data padrão (dia atual) na montagem
  useEffect(() => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    setDataInicio(`${ano}-${mes}-${dia}`);
    setDataFim(`${ano}-${mes}-${dia}`);
  }, []);

  // Buscar filas disponíveis ao montar e ao trocar de servidor
  useEffect(() => {
    if (isAdmin && !serverId) return; // admin aguarda o servidor ser definido
    buscarFilasDisponiveis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // Buscar filas disponíveis para o usuário
  const buscarFilasDisponiveis = async () => {
    setLoadingFilas(true);
    try {
      const query = isAdmin && serverId ? `?serverId=${serverId}` : '';
      const response = await axios.get(
        `${API_URL}/queue-reports/filas-disponiveis${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFilasDisponiveis(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao buscar filas:', err);
      setError('Erro ao carregar filas disponíveis');
    } finally {
      setLoadingFilas(false);
    }
  };

  // Buscar relatório com os filtros aplicados
  const buscarRelatorio = async (pagina = 1) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (filaSelecionada) params.append('fila', filaSelecionada);
      if (ramalSelecionado) params.append('ramal', ramalSelecionado);
      if (statusSelecionado) params.append('status', statusSelecionado);
      if (isAdmin && serverId) params.append('serverId', serverId);
      params.append('page', pagina);
      params.append('limit', 50);

      const response = await axios.get(
        `${API_URL}/queue-reports?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMetricas(response.data.data.metricas);
        setChamadas(response.data.data.chamadas);
        setPaginacao(response.data.data.paginacao);
      }
    } catch (err) {
      console.error('Erro ao buscar relatório:', err);
      setError(err.response?.data?.message || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  // Formatar tempo em segundos para HH:MM:SS
  const formatarTempo = (segundos) => {
    try {
      if (!segundos || segundos === 0 || segundos === '0') return '00:00:00';
      const seg = parseFloat(segundos);
      if (isNaN(seg)) return '00:00:00';
      const horas = Math.floor(seg / 3600);
      const minutos = Math.floor((seg % 3600) / 60);
      const segs = Math.floor(seg % 60);
      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;
    } catch (e) {
      return '00:00:00';
    }
  };

  // Exportar relatório para PDF
  const exportarPDF = () => {
    try {
      console.log('Iniciando exportação PDF...');
      
      if (!metricas) {
        setError('Não há métricas para exportar');
        return;
      }

      const doc = new jsPDF();
      let yPosition = 22;
      
      // Título
      doc.setFontSize(16);
      doc.text('Relatorio de Filas - Tempo de Atendimento', 14, yPosition);
      yPosition += 10;
      
      // Informações do relatório
      doc.setFontSize(9);
      doc.text(`Periodo: ${formatarDataISO(dataInicio)} ate ${formatarDataISO(dataFim)}`, 14, yPosition);
      yPosition += 5;
      
      if (filaSelecionada) {
        doc.text(`Fila: ${filaSelecionada}`, 14, yPosition);
        yPosition += 5;
      }
      
      doc.text(`Gerado em: ${formatarDataHora(new Date())}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Usuario: ${username || 'N/A'}`, 14, yPosition);
      yPosition += 10;
      
      // Métricas
      doc.setFontSize(12);
      doc.text('Metricas Gerais', 14, yPosition);
      yPosition += 7;
      
      doc.setFontSize(8);
      doc.text(`Total de Chamadas: ${metricas.total_chamadas || 0}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Chamadas Atendidas: ${metricas.chamadas_atendidas || 0} (${metricas.taxa_atendimento || 0}%)`, 14, yPosition);
      yPosition += 5;
      doc.text(`Chamadas Abandonadas: ${metricas.chamadas_abandonadas || 0} (${metricas.taxa_abandono || 0}%)`, 14, yPosition);
      yPosition += 5;
      doc.text(`Chamadas Nao Atendidas: ${metricas.chamadas_nao_atendidas || 0}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Tempo Medio de Espera: ${formatarTempo(metricas.tempo_medio_espera)}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Tempo Medio de Atendimento: ${formatarTempo(metricas.tempo_medio_atendimento)}`, 14, yPosition);
      yPosition += 5;
      doc.text(`Tempo Total Medio: ${formatarTempo(metricas.tempo_total_medio)}`, 14, yPosition);
      
      // Tabela de chamadas
      if (chamadas && chamadas.length > 0) {
        doc.addPage();
        doc.setFontSize(12);
        doc.text('Detalhamento de Chamadas', 14, 22);
        
        const tableData = chamadas.slice(0, 100).map(chamada => [
          formatarDataHora(chamada.calldate),
          String(chamada.origem || '-'),
          String(chamada.fila || '-'),
          formatarTempo(chamada.tempo_espera),
          formatarTempo(chamada.tempo_atendimento),
          formatarTempo(chamada.tempo_total),
          traduzirStatus(chamada.status)
        ]);
        
        autoTable(doc, {
          startY: 30,
          head: [['Data/Hora', 'Origem', 'Fila', 'Espera', 'Atend.', 'Total', 'Status']],
          body: tableData,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [102, 126, 234], fontSize: 8, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 20 },
            2: { cellWidth: 18 },
            3: { cellWidth: 20 },
            4: { cellWidth: 20 },
            5: { cellWidth: 20 },
            6: { cellWidth: 25 }
          },
          margin: { top: 30 }
        });
        
        if (chamadas.length > 100) {
          const finalY = doc.lastAutoTable.finalY || 30;
          doc.setFontSize(8);
          doc.text(`Nota: Exibindo apenas os primeiros 100 registros de ${chamadas.length} total.`, 14, finalY + 10);
        }
      }
      
      // Salvar PDF
      const nomeArquivo = `relatorio_filas_${formatarDataISO(dataInicio).replace(/\//g, '-')}_${Date.now()}.pdf`;
      doc.save(nomeArquivo);
      
      console.log('PDF exportado com sucesso!');
      
    } catch (error) {
      console.error('Erro detalhado ao exportar PDF:', error);
      setError(`Erro ao exportar PDF: ${error.message}`);
    }
  };

  // Exportar CSV/Excel (gerados no backend; baixa via axios com auth).
  const exportarArquivo = async (formato) => {
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (filaSelecionada) params.append('fila', filaSelecionada);
      if (ramalSelecionado) params.append('ramal', ramalSelecionado);
      if (statusSelecionado) params.append('status', statusSelecionado);
      if (isAdmin && serverId) params.append('serverId', serverId);

      const response = await axios.get(
        `${API_URL}/queue-reports/export/${formato}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const ext = formato === 'excel' ? 'xlsx' : 'csv';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_filas_${Date.now()}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || `Erro ao exportar ${formato}`);
    }
  };

  // Limpar filtros
  const limparFiltros = () => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    setDataInicio(`${ano}-${mes}-${dia}`);
    setDataFim(`${ano}-${mes}-${dia}`);
    setFilaSelecionada('');
    setRamalSelecionado('');
    setStatusSelecionado('');
    setMetricas(null);
    setChamadas([]);
    setError('');
  };

  // Preparar dados para gráficos
  const prepararDadosGraficos = () => {
    if (!metricas) return null;

    const dadosPizza = [
      { name: 'Atendidas', value: parseInt(metricas.chamadas_atendidas) || 0, color: '#48bb78' },
      { name: 'Abandonadas', value: parseInt(metricas.chamadas_abandonadas) || 0, color: '#ed8936' },
      { name: 'Não Atendidas', value: parseInt(metricas.chamadas_nao_atendidas) || 0, color: '#f56565' }
    ].filter(item => item.value > 0);

    const dadosBarras = [
      { name: 'Espera', tempo: parseFloat(metricas.tempo_medio_espera) || 0 },
      { name: 'Atendimento', tempo: parseFloat(metricas.tempo_medio_atendimento) || 0 },
      { name: 'Total', tempo: parseFloat(metricas.tempo_total_medio) || 0 }
    ];

    return { dadosPizza, dadosBarras };
  };

  // Renderizar gráficos
  const renderGraficos = () => {
    const dados = prepararDadosGraficos();
    if (!dados) return null;

    return (
      <div className="graficos-container">
        {dados.dadosPizza.length > 0 && (
          <div className="grafico-card">
            <h3>Distribuição de Chamadas por Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dados.dadosPizza}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {dados.dadosPizza.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grafico-card">
          <h3>Tempos Médios (segundos)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dados.dadosBarras}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="tempo" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Renderizar cards de métricas
  const renderMetricas = () => {
    if (!metricas) return null;

    return (
      <div className="metricas-container">
        <div className="metrica-card">
          <div className="metrica-titulo">Total de Chamadas</div>
          <div className="metrica-valor">{metricas.total_chamadas || 0}</div>
        </div>

        <div className="metrica-card success">
          <div className="metrica-titulo">Atendidas</div>
          <div className="metrica-valor">{metricas.chamadas_atendidas || 0}</div>
          <div className="metrica-percentual">{metricas.taxa_atendimento || 0}%</div>
        </div>

        <div className="metrica-card warning">
          <div className="metrica-titulo">Abandonadas</div>
          <div className="metrica-valor">{metricas.chamadas_abandonadas || 0}</div>
          <div className="metrica-percentual">{metricas.taxa_abandono || 0}%</div>
        </div>

        <div className="metrica-card danger">
          <div className="metrica-titulo">Não Atendidas</div>
          <div className="metrica-valor">{metricas.chamadas_nao_atendidas || 0}</div>
        </div>

        <div className="metrica-card info">
          <div className="metrica-titulo">Tempo Médio de Espera</div>
          <div className="metrica-valor">{formatarTempo(metricas.tempo_medio_espera)}</div>
        </div>

        <div className="metrica-card info">
          <div className="metrica-titulo">Tempo Médio de Atendimento</div>
          <div className="metrica-valor">{formatarTempo(metricas.tempo_medio_atendimento)}</div>
        </div>

        <div className="metrica-card">
          <div className="metrica-titulo">Tempo Total Médio</div>
          <div className="metrica-valor">{formatarTempo(metricas.tempo_total_medio)}</div>
        </div>

        <div className="metrica-card">
          <div className="metrica-titulo">Tempo Máx. Espera</div>
          <div className="metrica-valor">{formatarTempo(metricas.tempo_maximo_espera)}</div>
        </div>
      </div>
    );
  };

  // Renderizar tabela de chamadas
  const renderTabela = () => {
    if (chamadas.length === 0) {
      return <div className="no-data">Nenhuma chamada encontrada com os filtros aplicados</div>;
    }

    return (
      <div className="table-container">
        <table className="queue-table">
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Origem</th>
              <th>Fila</th>
              <th>Tempo Espera</th>
              <th>Tempo Atendimento</th>
              <th>Tempo Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {chamadas.map((chamada, index) => (
              <tr key={index}>
                <td>{formatarDataHora(chamada.calldate)}</td>
                <td>{chamada.origem || '-'}</td>
                <td>{chamada.fila || '-'}</td>
                <td>{formatarTempo(chamada.tempo_espera)}</td>
                <td>{formatarTempo(chamada.tempo_atendimento)}</td>
                <td>{formatarTempo(chamada.tempo_total)}</td>
                <td>
                  <span className={`status-badge status-${(chamada.status || '').toLowerCase().replace(' ', '-')}`}>
                    {traduzirStatus(chamada.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Renderizar paginação
  const renderPaginacao = () => {
    if (paginacao.total_paginas <= 1) return null;

    const paginas = [];
    const paginaAtual = paginacao.pagina_atual;
    const totalPaginas = paginacao.total_paginas;

    if (paginaAtual > 3) {
      paginas.push(
        <button key={1} onClick={() => buscarRelatorio(1)}>1</button>
      );
      if (paginaAtual > 4) {
        paginas.push(<span key="dots1">...</span>);
      }
    }

    for (let i = Math.max(1, paginaAtual - 2); i <= Math.min(totalPaginas, paginaAtual + 2); i++) {
      paginas.push(
        <button
          key={i}
          className={i === paginaAtual ? 'active' : ''}
          onClick={() => buscarRelatorio(i)}
        >
          {i}
        </button>
      );
    }

    if (paginaAtual < totalPaginas - 2) {
      if (paginaAtual < totalPaginas - 3) {
        paginas.push(<span key="dots2">...</span>);
      }
      paginas.push(
        <button key={totalPaginas} onClick={() => buscarRelatorio(totalPaginas)}>
          {totalPaginas}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          disabled={paginaAtual === 1}
          onClick={() => buscarRelatorio(paginaAtual - 1)}
        >
          « Anterior
        </button>
        {paginas}
        <button
          disabled={paginaAtual === totalPaginas}
          onClick={() => buscarRelatorio(paginaAtual + 1)}
        >
          Próximo »
        </button>
        <span className="pagination-info">
          Mostrando {chamadas.length} de {paginacao.total} chamadas
        </span>
      </div>
    );
  };

  return (
    <div className="queue-report-container">
      <div className="report-header">
        <h1>📊 Relatório de Tempo de Atendimento - Filas</h1>
        <p className="report-subtitle">
          Análise detalhada de performance e métricas das filas de atendimento
        </p>
      </div>

      <div className="filters-section">
        <h2>Filtros</h2>
        
        {loadingFilas ? (
          <div className="loading-message">
            <div className="spinner-small"></div>
            <span>Carregando filas disponíveis...</span>
          </div>
        ) : (
          <>
            <div className="filters-grid">
              {isAdmin && availableServers.length > 0 && (
                <div className="filter-group">
                  <label htmlFor="qr-server">PBX:</label>
                  <select
                    id="qr-server"
                    value={serverId}
                    onChange={(e) => setServerId(e.target.value)}
                  >
                    {availableServers.map(server => (
                      <option key={server.id} value={server.id.toString()}>
                        {server.name || `PBX ${server.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label htmlFor="dataInicio">Data Início:</label>
                <input
                  type="date"
                  id="dataInicio"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="dataFim">Data Fim:</label>
                <input
                  type="date"
                  id="dataFim"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="fila">Fila:</label>
                <select
                  id="fila"
                  value={filaSelecionada}
                  onChange={(e) => setFilaSelecionada(e.target.value)}
                >
                  <option value="">Todas as Filas</option>
                  {filasDisponiveis.map((fila) => (
                    <option key={fila.fila} value={fila.fila}>
                      {fila.fila}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="ramal">Ramal:</label>
                <input
                  type="text"
                  id="ramal"
                  placeholder="Ex: 1001"
                  value={ramalSelecionado}
                  onChange={(e) => setRamalSelecionado(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  value={statusSelecionado}
                  onChange={(e) => setStatusSelecionado(e.target.value)}
                >
                  <option value="">Todos os Status</option>
                  <option value="ANSWERED">Atendidas</option>
                  <option value="ABANDONED">Abandonadas</option>
                  <option value="NO ANSWER">Não Atendidas</option>
                </select>
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn-primary" onClick={() => buscarRelatorio(1)} disabled={loading}>
                {loading ? '⏳ Gerando...' : '🔍 Gerar Relatório'}
              </button>
              <button className="btn-secondary" onClick={limparFiltros}>
                🗑️ Limpar Filtros
              </button>
              <button className="btn-success" onClick={exportarPDF} disabled={!metricas}>
                📥 Exportar PDF
              </button>
              <button className="btn-success" onClick={() => exportarArquivo('excel')} disabled={!metricas}>
                📊 Exportar Excel
              </button>
              <button className="btn-secondary" onClick={() => exportarArquivo('csv')} disabled={!metricas}>
                📄 Exportar CSV
              </button>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {metricas && (
        <>
          <div className="view-toggle">
            <button
              className={visuMode === 'cards' ? 'active' : ''}
              onClick={() => setVisuMode('cards')}
            >
              📊 Métricas
            </button>
            <button
              className={visuMode === 'table' ? 'active' : ''}
              onClick={() => setVisuMode('table')}
            >
              📋 Tabela
            </button>
            <button
              className={visuMode === 'charts' ? 'active' : ''}
              onClick={() => setVisuMode('charts')}
            >
              📈 Gráficos
            </button>
            <button
              className={visuMode === 'both' ? 'active' : ''}
              onClick={() => setVisuMode('both')}
            >
              📊📋 Todos
            </button>
          </div>

          {(visuMode === 'cards' || visuMode === 'both') && renderMetricas()}
          
          {(visuMode === 'charts' || visuMode === 'both') && renderGraficos()}
          
          {(visuMode === 'table' || visuMode === 'both') && (
            <>
              <div className="table-header">
                <h2>Detalhamento de Chamadas</h2>
              </div>
              {renderTabela()}
              {renderPaginacao()}
            </>
          )}
        </>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Gerando relatório...</p>
        </div>
      )}
    </div>
  );
};

export default QueueReport;
